/**
 * @file PageRecentVersions.tsx
 * @description «Последние» версии в панели «Публикации» (phase19.16 Р3а;
 * uiux-r3 §3.1): три строки «v7 12.10 14:32 Иван [Смотреть]» с заметкой
 * («возврат к v3»), заготовки строк при загрузке, ошибка с «Повторить» и
 * [Все версии (N) ›] — на весь экран.
 */

import { ChevronRight, Eye, RefreshCw } from "lucide-react";

import { Button } from "../../ui/button";
import { Skeleton } from "../../ui/skeleton";
import { StatusPill } from "../../ui/StatusPill";
import { useBuilder } from "../builder-context";
import { usePageRevisions } from "../../../hooks/site-builder/usePageRevisions";
import { revisionAuthor, revisionNote } from "../../../lib/site-builder/page-status";
import { formatShopDateTime } from "../../../lib/shop-time";

/** Сколько версий в «Последних». */
const RECENT = 3;

export function PageRecentVersions({ pageId, liveVersion }: { pageId: string; liveVersion: number | null }) {
  const b = useBuilder();
  const revisions = usePageRevisions(pageId, RECENT, liveVersion);
  // «на сайте» — только у видимой страницы; у скрытой живая версия покупателям не видна (QA Р3а D12).
  const shown = b.publishing.changes?.isPublished ?? true;

  if (revisions.error) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p className="type-caption text-on-surface-variant">{revisions.error}</p>
        <Button variant="outline" onClick={revisions.reload}>
          <RefreshCw aria-hidden />
          Повторить
        </Button>
      </div>
    );
  }
  if (revisions.loading && revisions.items.length === 0) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true" aria-label="Загружаем версии">
        {Array.from({ length: RECENT }, (_, i) => (
          <Skeleton key={i} className="h-12 rounded-lg" />
        ))}
      </div>
    );
  }
  if (revisions.items.length === 0) return null;

  return (
    <div className="flex flex-col gap-1">
      <span className="type-caption text-on-surface-variant">Последние</span>
      <ul className="flex flex-col">
        {revisions.items.map((r) => (
          <li key={r.version} className="flex min-h-12 min-w-0 flex-wrap items-center gap-x-2">
            {/* Дата и заметка переносятся, а не обрезаются (QA Р3а D4); длинная заметка — две строки с подсказкой.
                Узкая панель (QA Р3в): текст не уже 11rem — пометка и «Смотреть» уходят на следующую строку, а не рвут слова. */}
            <div className="flex min-w-[11rem] flex-1 flex-col py-1">
              <span className="type-body break-words text-on-surface">
                v{r.version} · {formatShopDateTime(r.publishedAt)} · {revisionAuthor(r)}
              </span>
              <span className="type-caption line-clamp-2 break-words text-on-surface-variant" title={revisionNote(r)}>
                {revisionNote(r)}
              </span>
            </div>
            {r.isLive ? <StatusPill tone={shown ? "success" : "neutral"}>{shown ? "на сайте" : "текущая · скрыта"}</StatusPill> : null}
            <Button variant="ghost" className="shrink-0" onClick={() => b.viewPageVersion(r)} aria-label={`Смотреть версию ${r.version}`}>
              <Eye aria-hidden />
              <span className="max-[419px]:sr-only">Смотреть</span>
            </Button>
          </li>
        ))}
      </ul>
      <Button variant="outline" className="self-start" onClick={b.openVersions}>
        Все версии ({revisions.total})
        <ChevronRight aria-hidden />
      </Button>
    </div>
  );
}
